import type { LucideIcon } from 'lucide-react';
import {
  ArrowLeftRight,
  Building2,
  Inbox,
  Network,
  ShieldCheck,
  Sparkles,
  SquarePlus,
  ToggleRight,
} from 'lucide-react';

import {
  pathToAdminAIUsage,
  pathToAdminFeatureFlags,
  pathToAdminFieldMappings,
  pathToAdminMeshConnectivity,
  pathToAdminMeshOutbox,
  pathToAdminProvisionTenant,
  pathToAdminStaff,
  pathToAdminTenants,
} from '@/navigation';
import { satisfiesTier, type StaffTier } from '@/types/control/staff';

/**
 * One entry per page in the cross-tenant admin area.
 *
 * Both the sidebar (`src/pages/app/_layout.tsx`) and the admin landing page
 * read this array, so a page is added in exactly one place. T07-T12 each append
 * one entry here — that is deliberate: the alternative is six tickets each
 * editing a route table and a nav array, which is six ways to conflict.
 */
export type AdminSection = {
  /** Stable key, used for React keys and tests. */
  key: string;
  label: string;
  /** One line, shown on the landing page beneath the label. */
  description: string;
  icon: LucideIcon;
  href: string;
  /**
   * The **minimum** tier that may see this section, not an exact match: a
   * superuser satisfies `tenant_creator`. The six migrated admin pages require
   * `superuser`; tenant provisioning (T05) requires `tenant_creator`, and a
   * superuser sees it too.
   */
  requires: Exclude<StaffTier, 'none'>;
};

/**
 * One entry per page.
 */
export const ADMIN_SECTIONS: AdminSection[] = [
  {
    key: 'field-mappings',
    label: 'Field mappings',
    description:
      'Map connector attribute names onto the normalized fields Hydden stores.',
    icon: ArrowLeftRight,
    href: pathToAdminFieldMappings(),
    requires: 'superuser',
  },
  {
    key: 'ai-usage',
    label: 'AI usage',
    description: 'Sessions, interactions and token consumption per tenant.',
    icon: Sparkles,
    href: pathToAdminAIUsage(),
    requires: 'superuser',
  },
  {
    key: 'mesh-outbox',
    label: 'Mesh outbox',
    description: 'Writes queued for mesh, and where they are backing up.',
    icon: Inbox,
    href: pathToAdminMeshOutbox(),
    requires: 'superuser',
  },
  {
    key: 'mesh-connectivity',
    label: 'Mesh connectivity',
    description:
      "Whether Control's processes can reach mesh, and who is registered.",
    icon: Network,
    href: pathToAdminMeshConnectivity(),
    requires: 'superuser',
  },
  {
    key: 'tenants',
    label: 'Tenants and users',
    description: 'Every tenant, its sync state, and who can sign in to it.',
    icon: Building2,
    href: pathToAdminTenants(),
    requires: 'superuser',
  },
  {
    key: 'tenant-provisioning',
    label: 'Tenant provisioning',
    description:
      'Create a tenant with its sign-in method and first admin, registered in mesh.',
    // Not Building2: a superuser sees that tile, the tenant listing, as well.
    icon: SquarePlus,
    href: pathToAdminProvisionTenant(),
    // The one section a tenant_creator may use (HYD-2113). A superuser
    // satisfies it too.
    requires: 'tenant_creator',
  },
  {
    key: 'staff',
    label: 'Staff',
    description:
      'Who holds superuser and tenant_creator access. Grant or revoke tenant_creator.',
    icon: ShieldCheck,
    href: pathToAdminStaff(),
    // control-api 403s a tenant_creator on every /api/admin/staff route, so
    // showing them the section would advertise a page that cannot load.
    requires: 'superuser',
  },
  {
    key: 'feature-flags',
    label: 'Feature flags',
    description: 'Platform capabilities enabled per tenant.',
    icon: ToggleRight,
    href: pathToAdminFeatureFlags(),
    requires: 'superuser',
  },
];

/**
 * The sections a given tier may see.
 *
 * `satisfiesTier` rather than equality: `requires` is a floor, so a superuser
 * sees tenant-provisioning sections as well as its own. A `none` tier satisfies
 * nothing, so no separate guard is needed for it.
 */
export function adminSectionsForTier(tier: StaffTier): AdminSection[] {
  return ADMIN_SECTIONS.filter((section) =>
    satisfiesTier(tier, section.requires)
  );
}
